import styled from 'styled-components';

import { BasicSubtitle, SmallParagraph, SmallSubtitle } from '@genially/design-system';

import { PANEL_BAR_TRACK, PANEL_SURFACE } from '../../../common/panelColors';

export const Wrapper = styled.div(({ theme: { size } }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: size.units(2),
  padding: size.units(2),
  borderRadius: size.units(1),
  backgroundColor: PANEL_SURFACE,
}));

/** The same heading the pie chart shows, so both readings are titled the same way. */
export const QuestionSubtitle = styled(BasicSubtitle)(({ theme: { color } }) => ({
  color: color.content.reversed.default(),
}));

export const BarRow = styled.div(({ theme: { size } }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: size.units(1),
}));

/**
 * A fixed box, so every row lines up whatever the picture's proportions are. The size is
 * written rather than stretched: a stretched image would first hand the row its own
 * natural height, and a tall picture would stretch the row instead of the other way round.
 */
export const BarImage = styled.img(({ theme: { color, shadow, size } }) => ({
  flexShrink: 0,
  height: size.units(5),
  width: '70px',
  border: `1px solid ${color.border.primary.default()}`,
  borderRadius: size.units(1),
  boxShadow: shadow.short,
  // The whole picture rather than a crop: an image survey's options are told apart by it.
  objectFit: 'contain',
}));

export const BarContent = styled.div(({ theme: { size } }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: size.half,
  minWidth: 0,
  flex: 1,
}));

export const BarHeader = styled.div(({ theme: { size } }) => ({
  display: 'flex',
  alignItems: 'baseline',
  justifyContent: 'space-between',
  gap: size.units(1),
}));

/**
 * The letter the option carries inside its picture on the slide, drawn the same way here:
 * a light disc on the dark panel, so a row and the option on screen read as one thing.
 */
export const BarLetter = styled(SmallSubtitle)(({ theme: { color, shadow, size } }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  alignSelf: 'center',
  flexShrink: 0,
  width: size.units(2),
  height: size.units(2),
  borderRadius: '100%',
  backgroundColor: color.background.primary.default(0.8),
  boxShadow: shadow.shortHover,
  color: color.content.primary.default(),
}));

/** Keeps the letter glued to the text it names, away from the figures on the right. */
export const BarTitle = styled.div(({ theme: { size } }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: size.units(1),
  minWidth: 0,
}));

export const BarLabel = styled(SmallParagraph)(({ theme: { color } }) => ({
  color: color.content.reversed.default(),
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}));

/** Holds the two figures on one baseline; the separator travels with the vote count. */
export const BarCount = styled.div(({ theme: { size } }) => ({
  display: 'flex',
  alignItems: 'baseline',
  gap: size.half,
  flexShrink: 0,
}));

export const BarVotes = styled(SmallSubtitle)(({ theme: { color } }) => ({
  color: color.content.reversed.default(0.7),
}));

export const BarPercentage = styled(BasicSubtitle)(({ theme: { color } }) => ({
  color: color.content.reversed.default(),
}));

export const BarTrack = styled.div(({ theme: { size } }) => ({
  width: '100%',
  height: size.unit,
  borderRadius: size.half,
  overflow: 'hidden',
  backgroundColor: PANEL_BAR_TRACK,
}));

interface BarFillProps {
  $percentage: number;
  $color: string;
}

export const BarFill = styled.div<BarFillProps>(
  ({ theme: { transition }, $percentage, $color }) => ({
    height: '100%',
    width: `${$percentage}%`,
    borderRadius: 'inherit',
    transition: transition.standard('width'),
    backgroundColor: $color,
  }),
);
